import { motion } from "motion/react";
import { Clock, Leaf, Heart, Shield } from "lucide-react";
import { siteData } from "../data/mock";

const icons = [Clock, Leaf, Heart, Shield];

export const AboutSection = () => {
  const { about, images } = siteData;

  return (
    <section id="about" className="py-20 md:py-28" style={{ background: "#FFF9F2" }} aria-labelledby="about-heading">
      <div className="max-w-[1280px] mx-auto px-5 md:px-9">
        {/* Section label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-4"
        >
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#999999]">
            Our Approach
          </span>
        </motion.div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="rounded-xl overflow-hidden aspect-[4/3]">
              <img
                src={images.acupuncture}
                alt="Acupuncture treatment session"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="absolute -bottom-4 -right-2 md:right-6 rounded-xl px-5 py-3.5 shadow-lg"
              style={{
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
            >
              <p className="font-mono text-[11px] uppercase tracking-wider text-[#999999] mb-0.5">Every Session</p>
              <p
                className="text-[#232323] font-semibold text-lg"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                45 Minutes
              </p>
            </motion.div>
          </motion.div>

          {/* Text content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <h2
              id="about-heading"
              className="mb-5"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                lineHeight: 1.15,
                fontWeight: 600,
                color: "#232323",
                letterSpacing: "-0.01em",
              }}
            >
              {about.title}
            </h2>
            <p
              className="mb-5"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1rem",
                lineHeight: 1.7,
                color: "#353535",
              }}
            >
              {about.description}
            </p>
            <p
              className="mb-8"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.95rem",
                lineHeight: 1.7,
                color: "#353535",
              }}
            >
              {about.extendedDescription}
            </p>
            <button
              onClick={() => {
                const el = document.querySelector("#contact");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-[#232323] text-white border-none rounded-full px-6 py-3 font-mono text-[13px] font-medium uppercase tracking-wide cursor-pointer hover:bg-[#353535] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200"
            >
              Schedule a Visit
            </button>
          </motion.div>
        </div>

        {/* Highlights grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {about.highlights.map((item, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-xl p-5 hover:-translate-y-0.5 transition-transform duration-300"
                style={{
                  boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                  border: "1px solid rgba(0,0,0,0.04)",
                }}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center mb-3"
                  style={{ background: "rgba(255,244,232,0.8)" }}
                >
                  <Icon size={17} strokeWidth={1.5} color="#BCA182" />
                </div>
                <h4
                  className="mb-1"
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: "#232323",
                  }}
                >
                  {item.label}
                </h4>
                <p
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.8rem",
                    lineHeight: 1.5,
                    color: "#353535",
                    margin: 0,
                  }}
                >
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
